import React, { createContext, useContext, useState, useEffect } from 'react';
import authApi from '../services/authApi';
import errorTrackingService from '../services/errorTrackingService';
import { getTranslation } from '../utils/translations';
import logger from '../utils/logger';
import { clearArchive } from '../utils/archiveManager';

const AuthContext = createContext();

// Helper to get current language from localStorage
const getCurrentLanguage = () => {
  try {
    return localStorage.getItem('bookmarkManagerLanguage') || 'de';
  } catch {
    return 'de';
  }
};

// Helper to decode JWT and get expiration time
const getTokenExpiration = (token) => {
  try {
    if (!token || !token.includes('.')) return null;

    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Decode payload (second part)
    const payload = parts[1];
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decoded = JSON.parse(atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/')));

    if (decoded.exp) {
      return decoded.exp * 1000; // Convert to milliseconds
    }
    return null;
  } catch (e) {
    return null;
  }
};

// Check if token should be refreshed (less than 7 days until expiration)
const shouldRefreshToken = (token) => {
  const expiration = getTokenExpiration(token);
  if (!expiration) return false;

  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
  const timeUntilExpiration = expiration - now;

  // Refresh if less than 7 days remaining
  return timeUntilExpiration > 0 && timeUntilExpiration < sevenDaysMs;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Offline grace period: 8 hours in milliseconds
const OFFLINE_GRACE_PERIOD_MS = 8 * 60 * 60 * 1000;
// Backend check interval: 2 minutes
const BACKEND_CHECK_INTERVAL_MS = 2 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 📴 OFFLINE MODE: Track offline status
  const [isOffline, setIsOffline] = useState(false);
  const [offlineSince, setOfflineSince] = useState(null);
  const [backendCheckInterval, setBackendCheckInterval] = useState(null);

  // 🔒 SECURITY FIX: Continuous token monitoring
  const [tokenCheckInterval, setTokenCheckInterval] = useState(null);

  // 📴 OFFLINE MODE: Helper to detect network errors
  const isNetworkError = (err) => {
    if (!navigator.onLine) return true;
    if (!err) return false;

    const message = err.message?.toLowerCase() || '';
    const name = err.name?.toLowerCase() || '';

    return (
      name === 'typeerror' ||
      message.includes('fetch') ||
      message.includes('network') ||
      message.includes('failed to fetch') ||
      message.includes('network request failed') ||
      message.includes('networkerror') ||
      message.includes('net::') ||
      message.includes('econnrefused') ||
      message.includes('enotfound') ||
      message.includes('timeout')
    );
  };

  // 📴 OFFLINE MODE: Check if backend is reachable
  const checkBackendConnection = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8181/api';
      // Add cache-busting and no-store to bypass Service Worker cache
      const response = await fetch(`${apiUrl}/health?_t=${Date.now()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        cache: 'no-store', // Bypass cache completely
        // Short timeout for quick detection
        signal: AbortSignal.timeout(5000)
      });
      return response.ok || response.status < 500;
    } catch (err) {
      return false;
    }
  };

  // 📴 OFFLINE MODE: Handle going offline
  const goOffline = () => {
    // Always set offline state (don't check isOffline due to closure issues)
    setIsOffline(true);
    setOfflineSince(prev => prev || Date.now()); // Only set if not already set
  };

  // 📴 OFFLINE MODE: Handle coming back online
  const goOnline = async () => {
    if (isOffline) {
      logger.debug('📶 Coming back online - backend reachable');
      setIsOffline(false);
      setOfflineSince(null);

      // Re-validate token when coming back online
      const token = localStorage.getItem('authToken');
      if (token && user) {
        try {
          await authApi.verifyToken(token);
          logger.debug('✅ Token still valid after reconnection');
        } catch (err) {
          // Token invalid - need to re-login
          logger.debug('❌ Token invalid after reconnection - logging out');
          logout();
          setError(getTranslation(getCurrentLanguage(), 'sessionExpiredSecurity'));
        }
      }
    }
  };

  // 📴 OFFLINE MODE: Check if grace period exceeded
  const isGracePeriodExceeded = () => {
    if (!offlineSince) return false;
    return (Date.now() - offlineSince) > OFFLINE_GRACE_PERIOD_MS;
  };

  // 🔒 SECURITY FIX: Enhanced JWT token validation with fake token detection
  const validateTokenSecurity = (token) => {
    if (!token) return false;
    
    // Check minimum length
    if (token.length < 20) {
      return false;
    }
    
    // 🚨 CRITICAL: Check for Base64 JSON fake tokens (biggest vulnerability)
    try {
      // If token decodes to valid JSON, it's likely a fake token
      const decoded = atob(token);
      const parsed = JSON.parse(decoded);
      
      // Fake tokens often contain user data directly
      if (parsed && typeof parsed === 'object') {
        if (parsed.email || parsed.id || parsed.name || parsed.timestamp || parsed.userId || parsed.user) {
          return false;
        }
      }
    } catch (e) {
      // Good - proper tokens should not decode as simple Base64 JSON
    }
    
    // JWT tokens must have 3 parts separated by dots (header.payload.signature)
    if (token.includes('.')) {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }
      
      // Basic validation - just check parts exist and have minimum length
      try {
        for (let i = 0; i < parts.length; i++) {
          if (!parts[i] || parts[i].length < 4) {
            return false;
          }
        }
        
        // Try to parse header - but don't fail if it's not parseable (more lenient)
        try {
          const headerPart = parts[0];
          const paddedHeader = headerPart + '='.repeat((4 - headerPart.length % 4) % 4);
          const header = JSON.parse(atob(paddedHeader.replace(/-/g, '+').replace(/_/g, '/')));
          
          // Only check basic JWT structure, don't enforce strict format
          if (header && typeof header === 'object') {
            // This looks like a proper JWT header, allow it
            return true;
          }
        } catch (headerErr) {
          // If header parsing fails, still allow the token if it has the right structure
        }
      } catch (e) {
        return true; // Be more lenient - allow tokens that might be valid but hard to parse
      }

      return true;
    }
    // Dev tokens for backwards compatibility (legacy)
    else if (token.startsWith('dev-token-')) {
      return true;
    }

    return false;
  };

  // Check for existing authentication on app load with enhanced security validation
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          // 🔒 CRITICAL SECURITY: First validate token format locally (no network needed)
          if (!validateTokenSecurity(token)) {
            // Track security violation
            errorTrackingService.trackCustomError(
              'SECURITY_VIOLATION',
              'Invalid token format detected during auth initialization',
              {
                component: 'AuthContext',
                action: 'token_validation',
                tokenLength: token?.length || 0,
                severity: 'critical'
              }
            );

            localStorage.removeItem('authToken');
            setError('Security violation: Invalid token format detected.');
            setLoading(false);
            return;
          }

          // 📴 OFFLINE MODE: Try backend verification, but handle network errors gracefully
          let securityCheck = null;
          let userData = null;
          let backendReachable = true;

          try {
            // 🔒 ENHANCED SECURITY: Verify token authenticity with backend
            securityCheck = await authApi.verifyTokenSecurity(token);

            // 📴 OFFLINE MODE: Check if verifyTokenSecurity returned network_error
            if (securityCheck.reason === 'network_error' || securityCheck.status === 0 || securityCheck.status === 503) {
              logger.debug('📴 Backend not reachable (verifyTokenSecurity) - going offline');
              backendReachable = false;
              goOffline();
            } else if (!securityCheck.valid) {
              // Real security validation failure
              localStorage.removeItem('authToken');
              setError(`Security validation failed: ${securityCheck.message}`);
              setLoading(false);
              return;
            } else {
              // Backend reachable and token valid - get user data
              userData = await authApi.verifyToken(token);
            }
          } catch (backendErr) {
            // 📴 Check if this is a network error
            if (isNetworkError(backendErr)) {
              logger.debug('📴 Backend not reachable during init - going offline');
              backendReachable = false;
              goOffline();
            } else {
              // Real backend error (auth failed, etc.)
              throw backendErr;
            }
          }

          // 📴 OFFLINE MODE: If backend not reachable, restore user from token
          if (!backendReachable) {
            try {
              const parts = token.split('.');
              if (parts.length === 3) {
                const payload = parts[1];
                const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
                const decoded = JSON.parse(atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/')));

                const offlineUser = {
                  id: decoded.userId || decoded.id || decoded.sub,
                  email: decoded.userEmail || decoded.email || decoded.preferred_username || decoded.username || decoded.sub,
                  name: decoded.userName || decoded.name || decoded.given_name || 'Offline User'
                };

                if (offlineUser.id && offlineUser.email) {
                  setUser(offlineUser);
                  logger.debug('📴 Restored user from token for offline mode:', offlineUser.email);
                  setLoading(false);
                  return;
                }
              }
            } catch (decodeErr) {
              logger.debug('Could not decode token for offline mode');
            }
            setLoading(false);
            return;
          }

          // Backend was reachable - process userData normally

          // Check for various possible response structures
          let normalizedUser = null;
          let validationError = null;

          if (userData) {
            // Pattern 1: Direct user object with email and id
            if (userData.email && userData.id) {
              normalizedUser = {
                id: userData.id,
                email: userData.email,
                name: userData.name || userData.displayName || userData.firstName,
                onboardingCompleted: userData.onboardingCompleted
              };
            }
            // Pattern 2: User object with nested user data
            else if (userData.user && userData.user.email && userData.user.id) {
              normalizedUser = {
                id: userData.user.id,
                email: userData.user.email,
                name: userData.user.name || userData.user.displayName || userData.user.firstName,
                onboardingCompleted: userData.user.onboardingCompleted
              };
            }
            // Pattern 3: Username instead of email
            else if (userData.username && userData.id) {
              normalizedUser = {
                id: userData.id,
                email: userData.username,
                name: userData.name || userData.displayName || userData.firstName,
                onboardingCompleted: userData.onboardingCompleted
              };
            }
            // Pattern 4: Alternative field names
            else if (userData.userId && (userData.email || userData.username)) {
              normalizedUser = {
                id: userData.userId,
                email: userData.email || userData.username,
                name: userData.name || userData.displayName || userData.firstName,
                onboardingCompleted: userData.onboardingCompleted
              };
            }
            // Pattern 5: JWT payload structure (sub, preferred_username)
            else if (userData.sub && (userData.preferred_username || userData.email)) {
              normalizedUser = {
                id: userData.sub,
                email: userData.email || userData.preferred_username,
                name: userData.name || userData.given_name || userData.family_name,
                onboardingCompleted: userData.onboardingCompleted
              };
            }
            else {
              validationError = 'No valid user data pattern matched';
            }
          } else {
            validationError = 'Backend returned null/undefined user data';
          }
          
          if (normalizedUser) {
            // set normalized user
            setUser(normalizedUser);

            // Set user context for error tracking (use opaque id only)
            if (normalizedUser.id) {
              errorTrackingService.setUserId(normalizedUser.id);
            }

            // 🔄 AUTO-REFRESH: Refresh token if it expires in less than 7 days
            // This ensures users stay logged in (Spotify-like experience)
            if (shouldRefreshToken(token)) {
              try {
                const refreshResult = await authApi.refreshToken();
                if (refreshResult && refreshResult.token) {
                  logger.debug('Token automatically refreshed for extended session');
                }
              } catch (refreshErr) {
                // Silent fail - don't logout, just continue with current token
                logger.debug('Token refresh skipped:', refreshErr.message);
              }
            }
          } else {
            // Track invalid user data response (no sensitive data logged)
            errorTrackingService.trackCustomError(
              'AUTH_INVALID_USER_DATA',
              `User data validation failed: ${validationError}`,
              {
                component: 'AuthContext',
                action: 'user_data_validation',
                severity: 'critical'
              }
            );

            localStorage.removeItem('authToken');
            setError(`Security violation: ${validationError}`);
          }
        }
      } catch (err) {
        // This catch now only handles unexpected errors (network errors handled earlier)
        logger.error('Auth initialization error:', err);

        // Check if it's still a network error we missed
        if (isNetworkError(err)) {
          logger.debug('📴 Network error in outer catch - going offline');
          goOffline();

          // Try to restore user from token
          const token = localStorage.getItem('authToken');
          if (token) {
            try {
              const parts = token.split('.');
              if (parts.length === 3) {
                const payload = parts[1];
                const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
                const decoded = JSON.parse(atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/')));

                const offlineUser = {
                  id: decoded.userId || decoded.id || decoded.sub,
                  email: decoded.userEmail || decoded.email || decoded.preferred_username || decoded.username || decoded.sub,
                  name: decoded.userName || decoded.name || decoded.given_name || 'Offline User'
                };

                if (offlineUser.id && offlineUser.email) {
                  setUser(offlineUser);
                  logger.debug('📴 Restored user from token for offline mode');
                }
              }
            } catch (decodeErr) {
              // Could not decode token
            }
          }
        } else {
          // Real auth error - clear token
          localStorage.removeItem('authToken');
          setError('Authentication failed. Please log in again.');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);
  
  // 🔒 SECURITY FIX: Continuous token validation to catch runtime token manipulation
  // 📴 OFFLINE MODE: Now with proper network error handling
  useEffect(() => {
    if (user && !tokenCheckInterval) {
      const interval = setInterval(async () => {
        const currentToken = localStorage.getItem('authToken');

        if (currentToken) {
          // Check for fake tokens injected at runtime (local check, no network needed)
          if (!validateTokenSecurity(currentToken)) {
            localStorage.removeItem('authToken');
            logout();
            setError('Security violation: Fake token detected and removed.');
            return;
          }

          // 📴 OFFLINE MODE: Skip backend validation if we know we're offline
          if (isOffline) {
            logger.debug('📴 Skipping backend token validation - offline mode');
            return;
          }

          // Validate with backend since user is authenticated
          try {
            const securityCheck = await authApi.verifyTokenSecurity(currentToken);

            // 📴 OFFLINE MODE: Check if verifyTokenSecurity returned network_error
            if (securityCheck.reason === 'network_error' || securityCheck.status === 0 || securityCheck.status === 503) {
              logger.debug('📴 Backend not reachable during token check - going offline');
              goOffline();
              return;
            }

            // If we got here, backend is reachable - make sure we're marked as online
            if (isOffline) {
              await goOnline();
            }

            if (!securityCheck.valid) {
              logout();
              setError(`Security violation: ${securityCheck.message}`);
              return;
            }

            // 🔄 AUTO-REFRESH: Check if token should be refreshed (< 7 days until expiration)
            if (shouldRefreshToken(currentToken)) {
              try {
                await authApi.refreshToken();
                logger.debug('Token automatically refreshed during session');
              } catch (refreshErr) {
                // Silent fail - continue with current token
              }
            }

            // Also verify user data is still valid
            try {
              await authApi.verifyToken(currentToken);
            } catch (verifyErr) {
              // Check if network error
              const isNetworkErr = isNetworkError(verifyErr);
              if (isNetworkErr) {
                goOffline();
              } else if (verifyErr.message && (verifyErr.message.includes('token') || verifyErr.message.includes('auth') || verifyErr.message.includes('401') || verifyErr.message.includes('403'))) {
                logout();
                setError(getTranslation(getCurrentLanguage(), 'sessionExpiredSecurity'));
              }
            }
          } catch (err) {
            // 📴 OFFLINE MODE: Check if this is a network error
            const isNetworkErr = isNetworkError(err);

            if (isNetworkErr) {
              // Network error - go offline but DON'T logout
              logger.debug('📴 Network error during token check - going offline');
              goOffline();
            } else if (err.message && (err.message.includes('token') || err.message.includes('auth') || err.message.includes('401') || err.message.includes('403'))) {
              // Real auth error - logout
              logout();
              setError(getTranslation(getCurrentLanguage(), 'sessionExpiredSecurity'));
            }
            // Other errors are silently ignored
          }
        } else {
          // Token was removed but user still set - logout
          logout();
        }
      }, 30000); // Check every 30 seconds

      setTokenCheckInterval(interval);
    }

    // Cleanup interval when user logs out
    return () => {
      if (tokenCheckInterval) {
        clearInterval(tokenCheckInterval);
        setTokenCheckInterval(null);
      }
    };
  }, [user, tokenCheckInterval, isOffline]);
  
  // 🔒 SECURITY FIX: Monitor localStorage changes for token manipulation
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'authToken') {
        const newToken = e.newValue;
        if (newToken && !validateTokenSecurity(newToken)) {
          localStorage.removeItem('authToken');
          logout();
          setError('Invalid token detected. Please log in again.');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 📴 OFFLINE MODE: Regular backend connectivity check
  useEffect(() => {
    if (!user) return;

    const checkConnectivity = async () => {
      const isBackendReachable = await checkBackendConnection();

      if (isBackendReachable) {
        if (isOffline) {
          await goOnline();
        }
      } else {
        goOffline();

        // Check if grace period exceeded (8 hours)
        if (isGracePeriodExceeded()) {
          logger.debug('⏰ Offline grace period exceeded (8h) - logging out');
          logout();
          setError(getTranslation(getCurrentLanguage(), 'offlineTimeout') ||
            'Du warst zu lange offline. Bitte melde dich erneut an.');
        }
      }
    };

    // Check immediately
    checkConnectivity();

    // Set up interval for regular checks (every 2 minutes)
    const interval = setInterval(checkConnectivity, BACKEND_CHECK_INTERVAL_MS);
    setBackendCheckInterval(interval);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [user, isOffline, offlineSince]);

  // 📴 OFFLINE MODE: Listen to browser online/offline events
  useEffect(() => {
    const handleOnline = async () => {
      logger.debug('🌐 Browser reports online');
      const isBackendReachable = await checkBackendConnection();
      if (isBackendReachable) {
        await goOnline();
      }
    };

    const handleOffline = () => {
      logger.debug('🌐 Browser reports offline');
      goOffline();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isOffline]);
  
  // 🔒 SECURITY FIX: Override localStorage setItem to catch direct manipulation
  useEffect(() => {
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      if (key === 'authToken') {
        if (!validateTokenSecurity(value)) {
          setError('Security violation: Invalid token format detected.');
          return;
        }
      }
      originalSetItem.apply(this, arguments);
    };
    
    return () => {
      localStorage.setItem = originalSetItem;
    };
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authApi.login(email, password);
      const { user: userData, token } = response;
      
      // 🔒 SECURITY FIX: Validate received token before storing
      if (!validateTokenSecurity(token)) {
        throw new Error('Invalid token received from server');
      }
      
      // 🔒 SECURITY FIX: Validate user data structure
      if (!userData || !userData.email || !userData.id) {
        throw new Error('Invalid user data received from server');
      }
      
      localStorage.setItem('authToken', token);
      setUser(userData);
      
      // Set user context for error tracking on successful login (opaque id only)
      if (userData.id) {
        errorTrackingService.setUserId(userData.id);
      }
      
      // Track successful login (no PII)
      errorTrackingService.trackCustomError(
        'LOGIN_SUCCESS',
        'User successfully logged in',
        {
          component: 'AuthContext',
          action: 'user_login',
          userRef: userData.id,
          severity: 'info'
        }
      );
      
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      
      // Send mobile diagnostics to backend (no console logging)
      try {
        const { collectFullDiagnostics } = await import('../utils/browserDetection');
        const diagnostics = collectFullDiagnostics();

        // Send detailed mobile diagnostics to backend with API key
        if (diagnostics.mobile.isMobile) {
          const trackingApiKey = process.env.REACT_APP_ERROR_TRACKING_KEY;
          if (trackingApiKey) {
            fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8181/api'}/error-tracking`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Error-Tracking-Key': trackingApiKey
              },
              body: JSON.stringify({
                type: 'MOBILE_LOGIN_FAILURE',
                error: errorMessage,
                diagnostics,
                timestamp: new Date().toISOString()
              })
            }).catch(() => {});
          }
        }
      } catch (trackingError) {
        // Silently ignore tracking errors
      }
      
      setError(errorMessage);
      
      // Track login failure with error tracking service
      errorTrackingService.trackCustomError(
        'LOGIN_FAILED',
        `Login failed: ${errorMessage}`,
        {
          component: 'AuthContext',
          action: 'user_login_failed',
          errorMessage: errorMessage,
          severity: 'high'
        }
      );
      
      // 🔒 SECURITY FIX: Clear any potentially compromised token
      localStorage.removeItem('authToken');
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await authApi.register(email, password, name);
      const { user: userData, token } = response;
      
      localStorage.setItem('authToken', token);
      setUser(userData);

      // Set user context for error tracking on successful registration (opaque id only)
      if (userData.id) {
        errorTrackingService.setUserId(userData.id);
      }

      // 🧭 ONBOARDING: userData.onboardingCompleted kommt direkt vom Server
      // (false für frisch registrierte Nutzer). App.js zeigt anhand dessen
      // den Onboarding-Wizard; completeOnboarding() unten meldet den Abschluss zurück.

      // Track successful registration (no PII)
      errorTrackingService.trackCustomError(
        'REGISTRATION_SUCCESS',
        'User successfully registered',
        {
          component: 'AuthContext',
          action: 'user_registration',
          userRef: userData.id,
          severity: 'info'
        }
      );
      
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Track logout event
    if (user?.email) {
      errorTrackingService.trackCustomError(
        'LOGOUT_SUCCESS',
        'User successfully logged out',
        {
          component: 'AuthContext',
          action: 'user_logout',
          userId: user.email,
          severity: 'info'
        }
      );
    }
    
    // Privacy: Archiv-Snapshots (Papierkorb/Sicherungskopien) sind streng
    // account-gebunden und dürfen auf einem gemeinsam genutzten oder
    // öffentlichen Gerät (z.B. Internet-Café) nach dem Logout nicht mehr
    // auslesbar bleiben - localStorage ist nicht account-verschlüsselt,
    // nur per Schlüssel-Namenskonvention getrennt. Zielarchitektur ist,
    // dass das Archiv ohnehin serverseitig liegt (siehe TODO_Archiv_Backend.md)
    // und der Client es bei Bedarf/beim nächsten Login neu abruft - lokales
    // Löschen beim Logout ist damit kein dauerhafter Datenverlust, sobald
    // die Backend-Anbindung steht. Bis dahin (Übergangsphase, rein lokale
    // Speicherung) bedeutet das: das Archiv ist nach jedem Logout leer.
    if (user?.email) {
      clearArchive(user.email);
    }

    localStorage.removeItem('authToken');
    setUser(null);
    setError(null);
  };

  const clearError = () => {
    setError(null);
  };

  // 🧭 ONBOARDING: Meldet dem Backend, dass der Onboarding-Wizard erledigt
  // ist (regulär abgeschlossen oder übersprungen) - POST /api/auth/onboarding-complete,
  // idempotent. Aktualisiert den lokalen User-State optimistisch, damit die
  // Onboarding-Ansicht sofort verschwindet, auch falls der Request selbst noch unterwegs ist.
  const completeOnboarding = async () => {
    setUser(prev => (prev ? { ...prev, onboardingCompleted: true } : prev));
    try {
      await authApi.completeOnboarding();
    } catch (err) {
      // Best effort: Netzwerkfehler blockieren den App-Zugang nicht. Bleibt
      // onboardingCompleted serverseitig auf false, zeigt der nächste Login
      // das Onboarding erneut - unschädlich, da idempotent.
      logger.debug('Onboarding-Abschluss konnte nicht an den Server gemeldet werden:', err.message);
    }
  };

  // 👤 PROFILE: Change email address (requires current password for confirmation)
  const updateEmail = async (newEmail, currentPassword) => {
    try {
      const response = await authApi.updateProfile({ email: newEmail, currentPassword });
      const updatedEmail = response?.user?.email || response?.email || newEmail;
      setUser(prev => (prev ? { ...prev, email: updatedEmail } : prev));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || 'Failed to update email' };
    }
  };

  // 👤 PROFILE: Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authApi.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || 'Failed to change password' };
    }
  };

  // 👤 PROFILE: Permanently delete the account and all associated data
  const deleteAccount = async (password) => {
    try {
      await authApi.deleteAccount(password);
      // Account and all its data are gone on the backend - clear local session
      logout();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || 'Failed to delete account' };
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    clearError,
    updateEmail,
    changePassword,
    deleteAccount,
    completeOnboarding,
    isAuthenticated: !!user,
    // 📴 OFFLINE MODE
    isOffline,
    offlineSince
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};